import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../../components/CustomButton";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomTable from "../../../../components/CustomTable";
import CustomTextField from "../../../../components/CustomTextField";
import TopBar from "../../../../components/TopBar/TopBar";
import {
  createSpecies,
  deleteSpecies,
  getSpeciesList,
  updateSpecies,
} from "../../../../redux/reducers/speciesSlice";
import { AppColors } from "../../../../util/AppColors";

const tblHdrs = [
  "sNo",
  "speciesName",
  "noOfBreeds",
  "createdBy",
  "createdDate",
  "actions",
];

function Species({ onSpeciesClick }) {
  const dispatch = useDispatch();

  const [search, setSearch] = useState("");
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [form, setForm] = useState({ speciesName: "" });
  const [delModVisible, setDelModVisible] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [showWarning, setShowWarning] = useState(false);

  const speciesState = useSelector((state) => state.species);
  const speciesList = speciesState.list;

  useEffect(() => {
    dispatch(getSpeciesList());
  }, [dispatch]);

  const handleAddOrUpdate = () => {
    if (!form.speciesName.trim()) return;

    if (editIndex !== null) {
      const species = speciesList[editIndex];
      dispatch(
        updateSpecies({
          speciesId: species.speciesId,
          speciesName: form.speciesName,
        }),
      );
    } else {
      dispatch(createSpecies(form));
    }

    handleCloseDrawer();
  };

  const handleEdit = (index) => {
    const species = speciesList[index];
    setForm({ speciesName: species.speciesName });
    setEditIndex(index);
    setOpenDrawer(true);
  };

  const handleOpenDelModal = (index) => {
    const species = speciesList[index];
    const breedsLength = species.breeds?.length || 0; // Default to 0 if breeds is undefined/null

    if (breedsLength > 0) {
      setShowWarning(true); // Show warning modal
      setDelModVisible(true);
      setDeleteIndex(index);
    } else {
      setShowWarning(false); // Show confirm modal
      setDelModVisible(true);
      setDeleteIndex(index);
    }
  };

  const handleDelConfirm = () => {
    const species = speciesList[deleteIndex];
    if (species) {
      dispatch(deleteSpecies({ speciesId: species.speciesId }));
    }
    setDelModVisible(false);
    setDeleteIndex(null);
  };

  const handleDelModClose = () => {
    setDelModVisible(false);
    setDeleteIndex(null);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setForm({ speciesName: "" });
    setEditIndex(null);
  };

  const filteredRows = speciesList.filter((row) =>
    row.speciesName.toLowerCase().includes(search.toLowerCase()),
  );
  // .sort((a, b) => new Date(a.createdDate) - new Date(b.createdDate));

  return (
    <Box>
      {/* Top section */}
      <TopBar sx={{ py: 1, minHeight: "unset" }}>
        <Container maxWidth="xl">
          <div className="flex-row">
            <div className="top-row-cen-con w100Per">
              <div className="w30Per">
                <CustomTextField
                  placeholder="Search"
                  name="name"
                  fullWidth
                  handleChange={(e) => setSearch(e.target.value)}
                  value={search}
                  backgroundColor={AppColors.lightBlue2}
                />
              </div>

              <div className="ml20">
                <CustomButton
                  text="Create"
                  onClick={() => setOpenDrawer(true)}
                />
              </div>
            </div>
          </div>
          <Typography className="font-medium fs12 gray28 mt5">
            Total Species: {filteredRows?.length}
          </Typography>
        </Container>
      </TopBar>

      {/* Table */}
      <CustomTable
        grey
        columns={tblHdrs}
        datas={filteredRows?.map((row, index) => ({
          sNo: index + 1,
          speciesName: (
            <div
              className="cursor blue-color fs14"
              onClick={() => onSpeciesClick?.(row?.speciesId)}
            >
              {row?.speciesName}
            </div>
          ),
          noOfBreeds: row?.breeds?.length ?? 0,
          createdBy: row?.userName ?? "-",
          createdDate: new Date(row?.createdDate).toLocaleDateString(),
          actions: (
            <div>
              <IconButton
                color="primary"
                size="small"
                onClick={() => handleEdit(index)}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                color="error"
                size="small"
                onClick={() => handleOpenDelModal(index)}
              >
                <DeleteIcon />
              </IconButton>
            </div>
          ),
        }))}
        maxHeight='68vh'
      />

      {/* Drawer for Create & Edit */}
      <Drawer anchor="right" open={openDrawer} onClose={handleCloseDrawer}>
        <Box p={3} width={300}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6" color="primary">
              {editIndex !== null ? "Edit Species" : "Create Species"}
            </Typography>
            <IconButton onClick={handleCloseDrawer} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <TextField
            fullWidth
            label="Species Name"
            value={form.speciesName}
            onChange={(e) => setForm({ ...form, speciesName: e.target.value })}
            margin="normal"
            InputLabelProps={{
              sx: {
                position: "absolute",
                top: "50%",
                left: "14px",
                transform: "translateY(-50%)",
                transition: "all 0.2s ease-out",
                "&.Mui-focused, &.MuiFormLabel-filled": {
                  top: "0px",
                  transform: "translateY(-50%) scale(0.75)",
                  transformOrigin: "top left",
                },
              },
            }}
          />
          <Button
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
            onClick={handleAddOrUpdate}
          >
            {editIndex !== null ? "Update" : "Add"}
          </Button>
        </Box>
      </Drawer>

      {/* Confirmation Modal */}
      <CustomModal
        open={delModVisible}
        onClose={handleDelModClose}
        header={showWarning ? "Cannot Delete" : "Delete"}
        modalWidth={30}
      >
        <Typography className="flex-center txt-regular fs14">
          {showWarning
            ? "This record cannot be deleted as there are breeds associated with it."
            : "Are you sure you want to delete this record?"}
        </Typography>

        {/* Show buttons only if it's a confirmation modal */}
        {!showWarning && (
          <div className="flex-center mb10 mt15">
            <div>
              <CustomButton text="Cancel" onClick={handleDelModClose} />
            </div>
            <div className="ml10">
              <CustomButton redBtn text="Confirm" onClick={handleDelConfirm} />
            </div>
          </div>
        )}
      </CustomModal>
    </Box>
  );
}

export default Species;
